/**
 * Runtime loader for the baked **effect animations** (ADR-0180) - the fireball /
 * explosion FX strips declared in the `effects` content registry and baked (or
 * stubbed) into `assets/baked/effects/<id>.{png,json}`. The exact mirror of
 * `playerArt.ts`'s glob discipline: the atlases + manifests are glob'd so the
 * importable set is filled by walking the SAME registry the converter/stub bake
 * from and can never drift, and an effect with missing files (an unbaked host
 * whose stubs pre-date the effect, or one the real converter warn-skipped for
 * absent art) simply loads as **absent** from the returned map - the consuming
 * render slice degrades to a Graphics-primitive fallback, never a crash.
 *
 * Nothing consumes this yet (Slice 2 is the bake-path groundwork); a later arena
 * fire-mage slice plays these clips.
 */
import { Assets, Rectangle, Texture } from 'pixi.js';
import { effects as effectDefs, type EffectId } from '../sim/content';
import type { BakedEffectManifest } from './bakedManifest';
import { byBaseName } from './mobileAssetUrls';

/** One effect frame: its atlas sub-texture plus its center anchor (px). */
export interface EffectFrame {
  texture: Texture;
  /** Frame CENTER offsets - effects draw centered, unlike body clips' UO anchors. */
  cx: number;
  cy: number;
}

/** One loaded effect strip: its frames in play order and the bake-resolved pacing. */
export interface EffectClip {
  frames: EffectFrame[];
  frameIntervalMs: number;
  /** Whether the strip uses ClassicUO's additive effect blend. */
  additive?: boolean;
}

/** Every loaded effect clip, keyed by registry id. Partial: unbaked ids are absent. */
export type EffectArt = Partial<Record<EffectId, EffectClip>>;

// The baked effect atlases + manifests, discovered like the equipment/mobile
// globs: Vite resolves these at build/test time against `assets/baked/effects/`,
// which the stub (or the real converter) fills by walking the same `effects`
// registry, so what can be imported here never drifts from what is baked.
const effectManifests = import.meta.glob('../../assets/baked/effects/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, BakedEffectManifest>;
const effectAtlasUrls = import.meta.glob('../../assets/baked/effects/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/**
 * Load every baked effect strip, keyed by registry id. Registry-driven over
 * `effects.all()`; an id whose manifest/atlas is missing on disk, or whose baked
 * strip is somehow empty, is simply left out of the map (graceful absence - the
 * caller falls back to Graphics primitives). Atlases sample nearest, matching
 * every other pixel-art load path.
 */
export async function loadEffectArt(): Promise<EffectArt> {
  const manifests = byBaseName(effectManifests);
  const urls = byBaseName(effectAtlasUrls);
  const art: EffectArt = {};
  await Promise.all(
    effectDefs.all().map(async (def) => {
      const manifest = manifests.get(def.id);
      const url = urls.get(def.id);
      if (!manifest || !url || manifest.frames.length === 0) return;
      const atlas = await Assets.load<Texture>(url);
      atlas.source.scaleMode = 'nearest';
      art[def.id as EffectId] = {
        frameIntervalMs: manifest.frameIntervalMs,
        additive: def.additive,
        frames: manifest.frames.map((f) => ({
          texture: new Texture({ source: atlas.source, frame: new Rectangle(f.x, f.y, f.w, f.h) }),
          cx: f.cx,
          cy: f.cy,
        })),
      };
    }),
  );
  return art;
}
